import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';

const transactionStatuses = [
  { value: 'offer_made', label: 'Offer Made' },
  { value: 'under_contract', label: 'Under Contract' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'appraisal', label: 'Appraisal' },
  { value: 'closing', label: 'Closing' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' }
];

export default function TransactionForm({ open, onClose, transaction, listings, agents, onSave }) {
  const [formData, setFormData] = useState({
    listing_id: '', agent_id: '', buyer_name: '', buyer_email: '', buyer_phone: '',
    seller_name: '', seller_email: '', offer_price: '', final_price: '',
    commission_amount: '', status: 'offer_made',
    offer_date: new Date().toISOString().split('T')[0], closing_date: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (transaction) {
      setFormData({
        ...transaction,
        offer_price: transaction.offer_price || '',
        final_price: transaction.final_price || '',
        commission_amount: transaction.commission_amount || ''
      });
    } else if (open) {
      setFormData({
        listing_id: '', agent_id: '', buyer_name: '', buyer_email: '', buyer_phone: '',
        seller_name: '', seller_email: '', offer_price: '', final_price: '',
        commission_amount: '', status: 'offer_made',
        offer_date: new Date().toISOString().split('T')[0], closing_date: '', notes: ''
      });
    }
  }, [transaction, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const dataToSave = {
      ...formData,
      offer_price: formData.offer_price ? Number(formData.offer_price) : null,
      final_price: formData.final_price ? Number(formData.final_price) : null,
      commission_amount: formData.commission_amount ? Number(formData.commission_amount) : null
    };
    await onSave(dataToSave);
    setSaving(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={transaction?.id ? 'Edit Transaction' : 'New Transaction'}>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-2 gap-4">
          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Property Listing *</label>
            <select className="input select" value={formData.listing_id} onChange={(e) => handleChange('listing_id', e.target.value)} required>
              <option value="">Select listing</option>
              {listings?.filter(l => l.status === 'active' || l.id === formData.listing_id).map(l => (
                <option key={l.id} value={l.id}>{l.title} - ${l.list_price?.toLocaleString()}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Agent *</label>
            <select className="input select" value={formData.agent_id} onChange={(e) => handleChange('agent_id', e.target.value)} required>
              <option value="">Select agent</option>
              {agents?.filter(a => a.status === 'active').map(a => (
                <option key={a.id} value={a.id}>{a.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Status</label>
            <select className="input select" value={formData.status} onChange={(e) => handleChange('status', e.target.value)}>
              {transactionStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div style={{ gridColumn: 'span 2', paddingTop: '1rem', borderTop: '1px solid var(--slate-200)' }}>
            <h4 className="font-medium text-slate-900 mb-3">Buyer Information</h4>
            <div className="grid grid-2 gap-4">
              <div>
                <label className="label">Buyer Name *</label>
                <input className="input" value={formData.buyer_name} onChange={(e) => handleChange('buyer_name', e.target.value)} required />
              </div>
              <div>
                <label className="label">Buyer Email</label>
                <input className="input" type="email" value={formData.buyer_email} onChange={(e) => handleChange('buyer_email', e.target.value)} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="label">Buyer Phone</label>
                <input className="input" value={formData.buyer_phone} onChange={(e) => handleChange('buyer_phone', e.target.value)} />
              </div>
            </div>
          </div>

          <div style={{ gridColumn: 'span 2', paddingTop: '1rem', borderTop: '1px solid var(--slate-200)' }}>
            <h4 className="font-medium text-slate-900 mb-3">Seller Information</h4>
            <div className="grid grid-2 gap-4">
              <div>
                <label className="label">Seller Name</label>
                <input className="input" value={formData.seller_name} onChange={(e) => handleChange('seller_name', e.target.value)} />
              </div>
              <div>
                <label className="label">Seller Email</label>
                <input className="input" type="email" value={formData.seller_email} onChange={(e) => handleChange('seller_email', e.target.value)} />
              </div>
            </div>
          </div>

          <div style={{ gridColumn: 'span 2', paddingTop: '1rem', borderTop: '1px solid var(--slate-200)' }}>
            <h4 className="font-medium text-slate-900 mb-3">Financial Details</h4>
            <div className="grid grid-3 gap-4">
              <div>
                <label className="label">Offer Price ($) *</label>
                <input className="input" type="number" value={formData.offer_price} onChange={(e) => handleChange('offer_price', e.target.value)} required />
              </div>
              <div>
                <label className="label">Final Price ($)</label>
                <input className="input" type="number" value={formData.final_price} onChange={(e) => handleChange('final_price', e.target.value)} />
              </div>
              <div>
                <label className="label">Commission ($)</label>
                <input className="input" type="number" value={formData.commission_amount} onChange={(e) => handleChange('commission_amount', e.target.value)} />
              </div>
            </div>
          </div>

          <div>
            <label className="label">Offer Date</label>
            <input className="input" type="date" value={formData.offer_date} onChange={(e) => handleChange('offer_date', e.target.value)} />
          </div>

          <div>
            <label className="label">Closing Date</label>
            <input className="input" type="date" value={formData.closing_date} onChange={(e) => handleChange('closing_date', e.target.value)} />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label className="label">Notes</label>
            <textarea className="input textarea" value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} rows={3} placeholder="Transaction notes..." />
          </div>
        </div>

        <div className="modal-footer" style={{ marginTop: '1.5rem' }}>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving && <div className="spinner" style={{ borderTopColor: 'white' }} />}
            {transaction?.id ? 'Update Transaction' : 'Create Transaction'}
          </button>
        </div>
      </form>
    </Modal>
  );
}